import { FC } from "react";

import { Stack } from "@mui/material";
import { UseFormReturn } from "react-hook-form";

import { StepSuggestedDonationAmountsFormData } from "./types";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardSwitch } from "@/dashboard-design-system/Switch/DashboardSwitch";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

interface PromoteMonthlyGivingBlockProps {
  form: UseFormReturn<StepSuggestedDonationAmountsFormData>;
}

export const PromoteMonthlyGivingBlock: FC<PromoteMonthlyGivingBlockProps> = ({ form }) => {
  const { t } = useTranslate();
  const { liveFormData, setLiveFormData } = useLiveFormEditorContext<"donation-form">();
  const isDefaultRecurring = form.watch("defaultAmountsFrequency") !== "oneTime";

  const content = (
    <Stack direction="row" alignItems="center" spacing={1} useFlexGap justifyContent="space-between">
      <DashboardTypography
        variant="subtitle1"
        vibe={isDefaultRecurring ? "text-neutral-quiet" : "text-neutral-intense"}
        fontWeight="bold"
      >
        {t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.promoteMonthlyGiving")}
        {!isDefaultRecurring && (
          <DashboardInformationTooltip
            alignOnText
            title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.promoteMonthlyGiving.tooltip")}
            slotProps={{ icon: { size: "small" } }}
            sx={{ marginLeft: 1 }}
          />
        )}
      </DashboardTypography>
      <DashboardSwitch
        checked={form.watch("promoteMonthlyGiving")}
        disabled={isDefaultRecurring}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          const checked = event.target.checked;
          form.setValue("promoteMonthlyGiving", checked, { shouldDirty: true });
          if (!liveFormData) {
            return;
          }
          setLiveFormData({ ...liveFormData, pushMonthlyPreviewTrigger: checked ? Date.now() : 0 });
        }}
      />
    </Stack>
  );

  if (isDefaultRecurring) {
    return (
      <DashboardTooltip
        title={t("dashboard", "liveFormEditor.stepSuggestedDonationAmounts.promoteMonthlyGiving.disabledTooltip")}
      >
        <div>{content}</div>
      </DashboardTooltip>
    );
  }

  return content;
};
